import React from 'react';
import PropTypes from 'prop-types';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { useMatchCardData } from './hooks/useMatchCardData';
import { useScoreUpdate } from './hooks/useScoreUpdate';
import { usePeriodEvent } from './hooks/usePeriodEvent';
import { MatchCardDesktop } from './MatchCardDesktop';
import { MatchCardMobile } from './MatchCardMobile';

// Matches finals round names ("Semi Finals", "Grand Final", etc.) rather than
// a hardcoded abbreviation list, so it holds for comps that name finals differently.
const FINALS_ROUND_NAME = /final/i;

// Shield standing in for a team not yet known: grey for finals, gold for the
// Grand Final, else DsTeamIcon's own default.
const getPlaceholderShield = (round) => {
    if (!FINALS_ROUND_NAME.test(round?.name ?? '')) {
        return '';
    }

    return round.abbreviation === 'GF' ? 'shield-gold' : 'shield-grey';
};

/**
 * Shared, cross-widget match card.
 *
 * @param {object} props - Props.
 * @param {object} props.matchInfo - The match object from the fixture list API.
 * @param {'auto'|'desktop'|'mobile'} [props.layout] - Layout override; 'auto' picks from viewport width.
 * @returns {React.ReactElement} The match card.
 */
export const MatchCard = ({ matchInfo, layout = 'auto' }) => {
    const { match, genericStatus, matchId } = useMatchCardData(matchInfo);
    const rawScoreUpdate = useScoreUpdate(matchId);
    const periodEvent = usePeriodEvent(matchId);
    const windowSize = useWindowSize();

    const isScoringHome = rawScoreUpdate.teamId === match.homeTeamPid;
    const scoreUpdate = rawScoreUpdate.show
        ? {
              show: true,
              team: isScoringHome ? 'home' : 'away',
              teamAbbr: isScoringHome ? match.homeTeamAbbr : match.awayTeamAbbr,
              type: rawScoreUpdate.type
          }
        : { show: false };

    const isPlaceholder =
        PULSE.app.common.team.isPlaceholderTeam(match.homeTeamPid) ||
        PULSE.app.common.team.isPlaceholderTeam(match.awayTeamPid);

    const isDesktop =
        layout === 'auto'
            ? windowSize.width >= PULSE.app.measurements.desktop
            : layout === 'desktop';
    const Layout = isDesktop ? MatchCardDesktop : MatchCardMobile;

    const layoutProps = {
        match,
        genericStatus,
        matchId,
        scoreUpdate,
        periodEvent,
        fallbackIcon: getPlaceholderShield(matchInfo.round)
    };

    const CardElement = isPlaceholder ? 'span' : 'a';
    const linkProps = isPlaceholder
        ? {}
        : {
              href: PULSE.app.common.url.getMatchURL(match),
              'aria-label': PULSE.app.common.matchAriaLabel(match)
          };

    // The hover/focus tints need `!` because the unlayered
    // `a { background-color: transparent }` in _normalize.scss outranks every
    // `@layer` declaration whatever its specificity.
    const interactiveClasses = isPlaceholder
        ? ''
        : 'hover:bg-state-surface-hovered! focus:bg-state-surface-focussed!';
    const layoutClasses = isDesktop
        ? 'items-center justify-center gap-2xs px-s'
        : 'flex-col items-start justify-start gap-0 px-0 overflow-hidden';

    return (
        <div className="afl-design-system match-card w-full bg-surface-container rounded-sm">
            <CardElement
                data-match-id={match.id}
                data-match-status={genericStatus}
                className={`flex w-full min-h-2xl rounded-sm shadow-1 no-underline label-l-semi-bold text-on-surface py-2xs ${layoutClasses} ${interactiveClasses}`}
                {...linkProps}
            >
                <Layout {...layoutProps} />
            </CardElement>
        </div>
    );
};

MatchCard.propTypes = {
    matchInfo: PropTypes.object.isRequired,
    layout: PropTypes.oneOf(['auto', 'desktop', 'mobile'])
};
